import { useCallback, useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import dayjs, { DayjsInput } from 'utils/dayjs'
import { Col, Container, Row } from 'react-grid-system'
import { Heading, JsonLD, Modal } from '~elements'
import styles from './CalendarSingleArtist.module.scss'
import type { CalendarSingleArtistProps } from '~types'
import { useEventsStore } from 'store/useEventsStore'
import { createEventStructuredData } from 'components/modules/Calendar/utils/variants/eventStructureData'
import { useEventsForArtist } from '../../hooks/useEventsForArtist'
import { MonthNav } from './elements/MonthNav/MonthNav'
import Month from './elements/Month/Month'
import SelectedDay from './elements/SelectedDay/SelectedDay'
import { useWindowSize } from 'utils/hooks'
import { Dayjs } from 'dayjs'
import Loader from '../CalendarMultiArtist/elements/Loader/Loader'
import { NEXT_IMAGE_QUALITY } from '~constants'
import { CALENDAR_EDP_NUMBER_OF_MONTHS } from 'constants/calendar'

const CalendarSingleArtist = ({
  calendarId,
  heading,
  hidePricing = false,
  seo,
  artist,
  eventDetailsDisclaimer,
  groupTicketsOfferText,
  theme = 'primary'
}: CalendarSingleArtistProps) => {
  const { isBreakpoint: isBelowMd } = useWindowSize('sm', '<')
  const { isBreakpoint: isBelowSm } = useWindowSize('xs', '<')

  const backgroundImageSrc = `/assets/CalendarSingleArtist-bg-${theme}-${isBelowSm ? 'mobile' : 'desktop'}.jpg`
  // Define start and end dates for the calendar single artist view
  const startDate = useMemo(() => dayjs(), [])
  const endDate = dayjs().add(CALENDAR_EDP_NUMBER_OF_MONTHS, 'months')

  // State for JSON-LD structured data
  const [jsonLdData, setJsonLdData] = useState<any | null>(null)
  const [openModal, setOpenModal] = useState(false)

  // Fetch events for the specified artist and date range
  const { loading } = useEventsForArtist({ startDate, endDate, artist })

  // Extract formatted events from the global store
  const {
    eventsForSingleArtist: events,
    eventsByDateForSingleArtist: eventsByDate,
    eventsByMonthForSingleArtist: eventsByMonth,
    selectedDateForSingleArtist: selectedDate,
    selectedEvent,
    activeShowId,
    setSelectedDateForSingleArtist,
    setSelectedEvent,
    setActiveShowId
  } = useEventsStore()

  const handleDateSelection = (date: DayjsInput) => {
    setSelectedDateForSingleArtist(date as Dayjs)
    if (!isBelowMd) return
    setOpenModal(true)
  }

  useEffect(() => {
    if (!selectedDate || !eventsByDate || !eventsByMonth || !artist) return
    const dayKey = dayjs(selectedDate as string).format('YYYY-MM-DD')
    const eventsForSelectedDay = eventsByDate[dayKey] || []
    setSelectedEvent({ events: eventsForSelectedDay, artistDetail: artist })
    setActiveShowId(eventsForSelectedDay[0]?.id || null)
  }, [selectedDate, eventsByDate, eventsByMonth, artist])

  useEffect(() => {
    if (!events || !eventsByDate || !eventsByMonth) return
    const structuredData = createEventStructuredData(seo, artist, events, hidePricing)
    setJsonLdData(structuredData ?? {})

    const monthKeys = Object.keys(eventsByMonth)

    let foundDate = null

    for (const monthKey of monthKeys) {
      const dateKeys = Object.keys(eventsByMonth[monthKey]).sort()

      foundDate = dateKeys.find(dateKey => Array.isArray(eventsByDate[dateKey]) && eventsByDate[dateKey].length > 0)

      if (foundDate) break // Exit early if we find a date with events
    }

    if (foundDate) {
      setSelectedDateForSingleArtist(dayjs(foundDate, 'YYYY-MM-DD'))
    } else {
      console.warn('No events found in any month.')
    }
  }, [eventsByDate, eventsByMonth])
  const handleTimeSelection = useCallback(
    (id: string) => {
      if (!selectedEvent) return
      const event = selectedEvent.events.find(e => e.id === id)
      if (event) {
        setActiveShowId(event.id)
      }
    },
    [selectedEvent]
  )

  const handleModalClose = () => {
    setOpenModal(false)
  }

  return eventsByDate && artist ? (
    <>
      {jsonLdData && <JsonLD data={jsonLdData} />}

      <div className={styles.calendar} id={calendarId}>
        <Container style={{ width: '100%' }}>
          {heading && (
            <Heading justify='center' level={2} noMarginTop>
              {heading}
            </Heading>
          )}
          <MonthNav isLoading={loading} />
          {loading ? (
            <div className={styles['loader-container']}>
              <Loader />
            </div>
          ) : (
            <Row>
              <Col md={6}>
                <Month handleDateSelection={handleDateSelection} month={dayjs(selectedDate as string, 'MMMM-YYYY')} />
              </Col>
              <Col md={6}>
                <div className={styles.selectedDay}>
                  <SelectedDay
                    activeShowId={activeShowId}
                    disclaimer={eventDetailsDisclaimer}
                    eventInfo={selectedEvent}
                    groupTicketsOfferText={groupTicketsOfferText}
                    onTimeSelect={id => handleTimeSelection(id)}
                  />
                </div>
              </Col>
            </Row>
          )}
        </Container>

        <Image
          src={backgroundImageSrc}
          alt='dark gradient'
          className={styles['backgroundImage']}
          fill
          style={{
            objectFit: 'cover'
          }}
          quality={NEXT_IMAGE_QUALITY}
        />
      </div>
      {/* Modal */}
      {openModal && selectedEvent && (
        <Modal
          isOpen={true}
          handleModalClose={handleModalClose}
          closeOnOutsideClick={false}
          width={'md-legacy'}
          backgroundColor='gray-900'
          overlayTheme='blur'
          hasBorder
        >
          <SelectedDay
            activeShowId={activeShowId}
            disclaimer={eventDetailsDisclaimer}
            eventInfo={selectedEvent}
            groupTicketsOfferText={groupTicketsOfferText}
            onTimeSelect={id => handleTimeSelection(id)}
          />
        </Modal>
      )}
    </>
  ) : (
    <></>
  )
}

export default CalendarSingleArtist
